'use client';

import { TrendingUp } from 'lucide-react';
import { Pie, PieChart, Sector } from 'recharts';
import { PieSectorDataItem } from 'recharts/types/polar/Pie';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent
} from '@/components/ui/chart';

export const description = 'A donut chart';

interface PieData {
  name: string;
  value: number;
  fill: string;
}

interface ChartPieDonutProps {
  data: PieData[];
  title?: string;
  description?: string;
  trendText?: string;
  innerRadius?: number;
  outerRadius?: number;
  strokeWidth?: number;
  activeIndex?: number;
  height?: string;
  showTrend?: boolean;
  showLegend?: boolean;
  legendPosition?: 'bottom' | 'right';
}

export default function ChartPieDonut({
  data,
  trendText = '',
  innerRadius = 30,
  outerRadius = 50,
  strokeWidth = 5,
  activeIndex = -1,
  height = '250px',
  showTrend,
  showLegend = true,
  legendPosition = 'bottom'
}: ChartPieDonutProps) {
  // 动态生成 chartConfig
  const chartConfig: ChartConfig = {
    ...data.reduce(
      (config, item) => {
        config[item.name] = {
          label: item.name.charAt(0).toUpperCase() + item.name.slice(1),
          color: item.fill
        };
        return config;
      },
      {} as Record<string, { label: string; color: string }>
    )
  };
  // 渲染图例组件
  const renderLegend = () => {
    if (!showLegend) return null;

    return (
      <div
        className={`flex ${
          legendPosition === 'right' ? 'flex-col gap-2' : 'flex-wrap justify-center gap-4'
        }`}
      >
        {data.map((item, index) => (
          <div key={`legend-${index}`} className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: item.fill }} />
            <span className="text-muted-foreground text-sm">
              {item.name.charAt(0).toUpperCase() + item.name.slice(1)}: {item.value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card className="flex h-full flex-col justify-center rounded-none border-none shadow-none">
      <CardContent
        className={`flex-1 pb-0 ${legendPosition === 'right' ? 'flex items-center gap-4' : ''}`}
      >
        <ChartContainer
          config={chartConfig}
          className={`aspect-square ${legendPosition === 'right' ? 'flex-1' : ''}`}
          style={{ maxHeight: height }}
        >
          {/* <ResponsiveContainer width="100%" height="100%"> */}
          <PieChart>
            <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              strokeWidth={strokeWidth}
              activeIndex={activeIndex}
              activeShape={({
                outerRadius: sectorOuterRadius = 0,
                ...props
              }: PieSectorDataItem) => <Sector {...props} outerRadius={sectorOuterRadius + 10} />}
            />
          </PieChart>
          {/* </ResponsiveContainer> */}
        </ChartContainer>
        {legendPosition === 'right' && renderLegend()}
      </CardContent>
      <CardFooter className="flex-col gap-2 text-sm">
        {showTrend && (
          <div className="flex items-center gap-2 font-medium leading-none">
            {trendText} <TrendingUp className="h-4 w-4" />
          </div>
        )}
        {legendPosition === 'bottom' && renderLegend()}
      </CardFooter>
    </Card>
  );
}
